/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import React from 'react';

import { jsx } from '@atlaskit/css';
import {
	DefaultExtensionProvider,
	type ExtensionManifest,
} from '@atlaskit/editor-common/extensions';
import { fg } from '@atlaskit/platform-feature-flags';
import { ROVO_SKILL_COMBINED_EXTENSION_KEY, ROVO_SKILL_EXTENSION_KEY, ROVO_SKILL_EXTENSION_TYPE } from '@atlassian/conversation-assistant-content-transformer/utils/skills';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import {
	defaultIcon,
	skillIcon,
	type SkillIconName,
} from '@atlassian/rovo-platform-ui-components/skills/skill-icons';
import { SkillTag } from '@atlassian/rovo-platform-ui-components/skills/SkillTag';
import { type Skill } from '@atlassian/rovo-platform-ui-components/skills/types';

const getSkillIcon = (iconName?: SkillIconName) =>
	iconName && skillIcon[iconName] ? skillIcon[iconName] : defaultIcon;

/**
 * Temporary type used for feature gating skills on the frontend
 */
export type SkillQuickInsert = Skill & {
	featureGateName?: string;
};

export const ADMIN_SKILLS_LIST: SkillQuickInsert[] = [
	{
		slug: 'invite-users',
		name: 'Invite users',
		description: `Invite new users to an app and role, or invite them to a group.`,
		product: 'Administration',
		iconName: 'person-add',
	},
	{
		slug: 'troubleshoot-invitation-errors',
		name: 'Troubleshoot invitation errors',
		description: `Get tailored help to resolve invitation errors.`,
		product: 'Administration',
		iconName: 'person-add',
	},

	{
		slug: 'grant-access',
		name: 'Grant access',
		description: `Grant existing users access to an app, role, or group.`,
		product: 'Administration',
		iconName: 'person-add',
	},
	{
		slug: 'troubleshoot-user-access',
		name: 'Troubleshoot user access',
		description: `Investigate user roles and permissions to resolve access issues.`,
		product: 'Administration',
		iconName: 'people-group',
	},
	{
		slug: 'troubleshoot-user-provisioning',
		name: 'Troubleshoot user provisioning',
		description: `Get help with user provisioning from domain claims to SCIM syncing.`,
		product: 'Administration',
		iconName: 'person-added',
	},
	{
		slug: 'troubleshoot-single-sign-on',
		name: 'Troubleshoot single sign-on',
		description: `Get help to resolve SAML single sign-on errors.`,
		product: 'Administration',
		iconName: 'login',
	},
	{
		slug: 'review-api-security',
		name: 'Review API security',
		description: `Review inactive API tokens and keys to improve endpoint security.`,
		product: 'Administration',
		iconName: 'lock-locked',
	},
];

// These three PJM skills require the feature gate to be enabled
const GATED_JIRA_SKILLS = ['create-work-items', 'update-work-items', 'write-status-update'];

const SKILL_FG_CHECKERS: Record<string, () => boolean> = {
	rovo_chat_plan_analysis_skill: () => fg('rovo_chat_plan_analysis_skill'),
	rovo_chat_plan_add_work_dependency_skill: () => fg('rovo_chat_plan_add_work_dependency_skill'),
	rovo_chat_create_plan_work_item_skill: () => fg('rovo_chat_create_plan_work_item_skill'),
};

const isSkillEnabled = (featureGateName?: string): boolean => {
	if (!featureGateName) {
		return true;
	}
	const checker = SKILL_FG_CHECKERS[featureGateName];
	if (checker) {
		return checker();
	}
	return true;
};

// TODO: remove after integration with backend
export const TEMP_SKILLS_LIST: SkillQuickInsert[] = [
	{
		slug: 'update-work-items',
		name: 'Update work items',
		description: 'Update work item fields like assignee, summary, descriptions, and more',
		product: 'Jira',
		iconName: 'work-item',
	},
	{
		slug: 'write-status-update',
		name: 'Write status update',
		description: 'Provide an update on the status of work in a space',
		product: 'Jira',
		iconName: 'ai-generative-text',
	},
	{
		slug: 'summarize-release',
		name: 'Summarize release',
		description: 'Summarize release scope, risks, blockers, and more',
		product: 'Jira',
		iconName: 'chart-bar-line',
	},
	{
		slug: 'draft-release-notes',
		name: 'Draft release notes',
		description: 'Write release notes covering key changes in a release',
		product: 'Jira',
		iconName: 'ai-generative-text',
	},
	{
		slug: 'create-release',
		name: 'Create release',
		description: 'Create a release to package code for deployment',
		product: 'Jira',
		iconName: 'add',
	},
	{
		slug: 'explain-next-status',
		name: 'Explain next status',
		description: 'Get a summary of available statuses and transitions for a work item',
		product: 'Jira',
		iconName: 'project-status',
	},
	{
		slug: 'get-data-insights',
		name: 'Get data insights',
		description: 'Create charts and get insights on your work',
		product: 'Jira',
		iconName: 'chart-pie',
	},
	{
		slug: 'manage-jira-permissions',
		name: 'Manage Jira permissions',
		description: `Manage permissions in Jira projects`,
		product: 'Jira',
		iconName: 'lock-locked',
	},
	{
		slug: 'find-jira-settings',
		name: 'Find Jira settings',
		description: `Get assistance finding the correct Jira admin settings for your needs`,
		product: 'Jira',
		iconName: 'settings',
	},
	{
		slug: 'assist-jira-plans',
		name: 'Assist Jira plans',
		description: 'Get help optimizing and managing Jira plans',
		product: 'Jira',
		iconName: 'plan',
	},
	{
		slug: 'summarize-plan',
		name: 'Summarize plan',
		description: 'Get detailed summaries of Jira plans',
		product: 'Jira',
		iconName: 'ai-generative-text-summary',
		featureGateName: 'rovo_chat_plan_analysis_skill',
	},
	{
		slug: 'create-plan-work-item',
		name: 'Create plan work item',
		description: 'Create work items within Jira plans',
		product: 'Jira',
		iconName: 'work-item',
		featureGateName: 'rovo_chat_create_plan_work_item_skill',
	},
	{
		slug: 'add-work-dependency',
		name: 'Add work dependency',
		description: 'Add dependencies between work items in Jira plans',
		product: 'Jira',
		iconName: 'transition',
		featureGateName: 'rovo_chat_plan_add_work_dependency_skill',
	},
	{
		slug: 'remove-work-dependency',
		name: 'Remove work dependency',
		description: 'Remove dependencies between work items in a Jira plan',
		product: 'Jira',
		iconName: 'transition',
		featureGateName: 'rovo_chat_jira_plans_remove_work_dependency_skill',
	},
	{
		slug: 'manage-plan-features',
		name: 'Manage plan features',
		description: 'Manage features in a Jira plan, i.e. releases',
		product: 'Jira',
		iconName: 'app-switcher',
		featureGateName: 'rovo_chat_jira_plans_manage_features_skill',
	},
	{
		slug: 'create-plan-release',
		name: 'Create plan release',
		description: 'Create a release for a Jira plan',
		product: 'Jira',
		iconName: 'release',
		featureGateName: 'rovo_chat_create_plan_release_skill',
	},
	{
		slug: 'create-program-board',
		name: 'Create program board',
		description: 'Create a program board for a Jira plan',
		product: 'Jira',
		iconName: 'board',
		featureGateName: 'rovo_chat_jira_plans_create_program_board_skill',
	},
	{
		slug: 'remove-program-board',
		name: 'Remove program board',
		description: 'Remove a program board from a Jira plan',
		product: 'Jira',
		iconName: 'board',
		featureGateName: 'rovo_chat_jira_plans_remove_program_board_skill',
	},
	{
		slug: 'add-work-source',
		name: 'Add work source',
		description: 'Add a work source to a Jira plan',
		product: 'Jira',
		iconName: 'playlist-add',
		featureGateName: 'rovo_chat_jira_plans_add_work_source_skill',
	},
	{
		slug: 'remove-work-source',
		name: 'Remove work source',
		description: 'Remove a work source from a Jira plan',
		product: 'Jira',
		iconName: 'playlist-remove',
		featureGateName: 'rovo_chat_jira_plans_remove_work_source_skill',
	},
	{
		slug: 'query-plan',
		name: 'Query plan',
		description: 'Query and retrieve information from Jira plans',
		product: 'Jira',
		iconName: 'search',
		featureGateName: 'rovo_chat_jira_plans_query_plan_skill',
	},
	{
		slug: 'search-assets',
		name: 'Search Assets',
		description: 'Query Assets data to get organizational insights in Rovo Chat',
		product: 'Jira',
		iconName: 'assets',
	},
];

const filterSkillsByFeatureGate = (skills: SkillQuickInsert[]): SkillQuickInsert[] => {
	const jiraSkillsEnabled = fg('rovo_jira_skills_enabled');
	const adminSkillsEnabled = fg('adminhub_rovo_skills_enabled');

	// Filter out gated Jira skills if the feature gate is not enabled
	let filteredSkills = jiraSkillsEnabled
		? skills
		: skills.filter((skill) => !GATED_JIRA_SKILLS.includes(skill.slug));

	// Filter out skills based on their individual feature gates evaluated at runtime.
	filteredSkills = filteredSkills.filter((skill) => isSkillEnabled(skill.featureGateName));

	// Prepend admin skills if the feature gate is enabled
	// This happens even if all skills were filtered out (filteredSkills is empty)
	if (adminSkillsEnabled) {
		return [...ADMIN_SKILLS_LIST, ...filteredSkills];
	}

	return filteredSkills;
};

type UseSkillsManifestProps = {
	skills: Skill[];
	isConversationAgentic: boolean;
	promptHasSkill: boolean;
	enableSkills?: boolean;
};

export const useSkillsManifest = ({
	skills,
	isConversationAgentic,
	promptHasSkill,
	enableSkills = true,
}: UseSkillsManifestProps): DefaultExtensionProvider<Skill>[] | undefined => {
	const { sendAnalyticsEvent } = useAnalytics();

	const createManifest = React.useCallback(
		(skills: Skill[]) => {
			const filteredSkills = filterSkillsByFeatureGate(skills);

			const manifest: ExtensionManifest<Skill> = {
				title: 'Skills',
				type: ROVO_SKILL_EXTENSION_TYPE,
				key: ROVO_SKILL_EXTENSION_KEY,
				categories: ['Skills'],
				icons: {
					'16': defaultIcon,
					'24': defaultIcon,
					'48': defaultIcon,
				},
				modules: {
					quickInsert: filteredSkills.map((skill) => {
						const { slug, description, name, product, iconName } = skill;
						return {
							key: slug,
							keywords: ['skills', 'skill', slug, product ?? ''],
							// 1 is the default priority
							priority: 99,
							title: name,
							description: description,
							icon: getSkillIcon(iconName),
							action: async () => {
								sendAnalyticsEvent({
									eventType: 'ui',
									action: 'clicked',
									actionSubject: 'button',
									actionSubjectId: 'rovoChatSkillSelected',
									attributes: {
										slashCommand: slug,
										skillProductKey: product,
									},
								});
								return [
									{
										type: 'inlineExtension',
										attrs: {
											extensionKey: ROVO_SKILL_COMBINED_EXTENSION_KEY,
											extensionType: ROVO_SKILL_EXTENSION_TYPE,
											parameters: skill,
										},
									},
									// render an empty space after the skill tag
									{ type: 'text', text: ' ' },
								];
							},
						};
					}),
					nodes: {
						skillTag: {
							type: 'inlineExtension',
							render:
								async () =>
								({ node }) => {
									const params = node.parameters;
									if (params) {
										return <SkillTag {...params} />;
									}
									return null;
								},
						},
					},
				},
			};

			return manifest;
		},
		[sendAnalyticsEvent],
	);

	const manifestProvider = React.useMemo(() => {
		// Return undefined if skills are disabled
		if (!enableSkills) {
			return undefined;
		}
		// skills are only applicable to non-agentic conversations
		// remove skills if a skill is already present in the prompt until chaining is allowed
		if ((isConversationAgentic || promptHasSkill) && fg('rovo_chat_enable_skills_ui_m1')) {
			return [new DefaultExtensionProvider([createManifest([])])];
		} else if (fg('rovo_chat_enable_skills_ui_m1')) {
			return [
				new DefaultExtensionProvider([
					createManifest(fg('rovo_chat_skills_fe_agg_cutover') ? skills : TEMP_SKILLS_LIST),
				]),
			];
		}
		return undefined;
	}, [isConversationAgentic, promptHasSkill, createManifest, enableSkills, skills]);

	return manifestProvider;
};
